import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  approveEducationRecord,
  approveExternalEducationReport,
  createEducationRecord,
  createExternalEducationReport,
  deleteEducationRecord,
  deleteEducationRecordAttachment,
  deleteExternalEducationReport,
  deleteExternalEducationReportAttachment,
  downloadEducationRecordAttachment,
  downloadExternalEducationReportAttachment,
  fetchEducationPlans,
  fetchEducationRecords,
  fetchExternalEducationReports,
  revokeEducationRecord,
  revokeExternalEducationReport,
  updateEducationRecord,
  updateExternalEducationReport,
  uploadEducationRecordAttachments,
  uploadExternalEducationReportAttachments,
  downloadEducationRecordPdf,
  downloadExternalEducationReportPdf,
} from "../../api/education";
import { fetchOrgMembers } from "../../api/org";
import { useCompanyStore } from "../../store/companyStore";
import {
  APPROVAL_TARGET_EDUCATION_RECORD,
  APPROVAL_TARGET_EXTERNAL_EDUCATION,
} from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import {
  createEmptyEducationAttendee,
  defaultEducationAttendees,
  EducationAnnualPlan,
  EducationAttendee,
  EducationRecord,
  EducationRecordAttachment,
  EducationRecordPayload,
  ExternalEducationReport,
  ExternalEducationReportAttachment,
  ExternalEducationReportPayload,
  parseEducationAttendees,
  serializeEducationAttendees,
} from "../../types/education";
import { OrgMemberSummary } from "../../types/org";
import { notifyError, reportError } from "../../utils/notify";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";
import SignaturePadField from "../common/SignaturePadField";

import EducationAnnualPlanTab from "./EducationAnnualPlanTab";
import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";
import "./CleanActivityPanel.css";
import "./EducationAnnualPlanTab.css";
import "./QualityShell.css";
import "./SmallGroupPanel.css";

function orgMemberName(m: OrgMemberSummary) {
  return m.displayName || m.nickname || `사용자 ${m.userId}`;
}

/** HH:mm:ss → HH:mm */
function toTimeInputValue(value?: string | null) {
  if (!value) return "";
  const t = value.trim();
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function formatEducationSchedule(
  date?: string | null,
  start?: string | null,
  end?: string | null,
) {
  const d = date?.trim() || "";
  const from = toTimeInputValue(start);
  const to = toTimeInputValue(end);
  if (!d && !from && !to) return "-";
  if (from && to) return `${d || '-'} ${from}~${to}`;
  if (from || to) return `${d || '-'} ${from || to}`;
  return d || "-";
}

const UNASSIGNED_DEPT = "__UNASSIGNED__";

type Tab = "plan" | "record" | "external";

type RecordView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; recordId: number }
  | { type: "detail"; recordId: number };

type ExtView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; reportId: number }
  | { type: "detail"; reportId: number };

const RECORD_LIST: RecordView = { type: "list" };
const EXT_LIST: ExtView = { type: "list" };

type ApprovalStamp = {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  canRevoke?: boolean | null;
  canConfirm?: boolean | null;
  canReopen?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
};

const EMPTY_STAMP: ApprovalStamp = {
  authorName: null,
  reviewerName: null,
  approverName: null,
  canRevoke: false,
  canConfirm: false,
  canReopen: false,
  approvalInProgress: false,
  pendingApprovalRequestId: null,
  canCancelApproval: false,
};

function formatSize(size?: number | null) {
  if (size == null || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeStatus(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  if (s === "COMPLETED" || s === "SUBMITTED") return "CONFIRMED";
  return s;
}

function statusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "결재 요청 중";
  const s = normalizeStatus(status);
  if (s === "APPROVED" || s === "CONFIRMED") return "승인";
  return "작성중";
}

function statusBadgeClass(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "pending";
  return normalizeStatus(status).toLowerCase();
}

function isDraft(status?: string | null) {
  return normalizeStatus(status) === "DRAFT";
}

function isApproved(status?: string | null) {
  const s = normalizeStatus(status);
  return s === "APPROVED" || s === "CONFIRMED";
}

function stampFrom(row: ApprovalStamp): ApprovalStamp {
  return {
    authorName: row.authorName ?? null,
    reviewerName: row.reviewerName ?? null,
    approverName: row.approverName ?? null,
    canRevoke: row.canRevoke ?? false,
    canConfirm: row.canConfirm ?? false,
    canReopen: row.canReopen ?? false,
    approvalInProgress: row.approvalInProgress ?? false,
    pendingApprovalRequestId: row.pendingApprovalRequestId ?? null,
    canCancelApproval: row.canCancelApproval ?? false,
  };
}

function apiErrorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message || fallback
  );
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

function EducationPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [tab, setTab] = useState<Tab>("plan");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");

  const [plans, setPlans] = useState<EducationAnnualPlan[]>([]);

  const [records, setRecords] = useState<EducationRecord[]>([]);
  const [recordKeyword, setRecordKeyword] = useState("");
  const [recordView, setRecordView] = useState<RecordView>(RECORD_LIST);
  const [recordHint, setRecordHint] = useState("");
  const [recordAttachments, setRecordAttachments] = useState<
    EducationRecordAttachment[]
  >([]);
  const [recordPendingFiles, setRecordPendingFiles] = useState<File[]>([]);
  const [recordStamp, setRecordStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [recordApprovalOpen, setRecordApprovalOpen] = useState(false);
  const recordFileRef = useRef<HTMLInputElement>(null);
  const [recordForm, setRecordForm] = useState<EducationRecordPayload>({
    recordNo: "",
    educationDate: new Date().toISOString().slice(0, 10),
    startTime: "",
    endTime: "",
    title: "",
    educationType: "INTERNAL",
    instructor: "",
    place: "",
    hours: "",
    attendees: "[]",
    content: "",
    resultText: "",
    status: "DRAFT",
    remark: "",
  });
  const [attendeeRows, setAttendeeRows] = useState<EducationAttendee[]>(
    defaultEducationAttendees(),
  );
  const [orgMembers, setOrgMembers] = useState<OrgMemberSummary[]>([]);

  const [externals, setExternals] = useState<ExternalEducationReport[]>([]);
  const [extKeyword, setExtKeyword] = useState("");
  const [extView, setExtView] = useState<ExtView>(EXT_LIST);
  const [extHint, setExtHint] = useState("");
  const [extAttachments, setExtAttachments] = useState<
    ExternalEducationReportAttachment[]
  >([]);
  const [extPendingFiles, setExtPendingFiles] = useState<File[]>([]);
  const [extStamp, setExtStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [extApprovalOpen, setExtApprovalOpen] = useState(false);
  const extFileRef = useRef<HTMLInputElement>(null);
  const [extForm, setExtForm] = useState<ExternalEducationReportPayload>({
    reportNo: "",
    courseName: "",
    organizer: "",
    periodFrom: "",
    periodTo: "",
    traineeName: "",
    department: "",
    hours: "",
    costText: "",
    content: "",
    applicationPlan: "",
    status: "DRAFT",
    remark: "",
  });

  const recordEditId =
    recordView.type === "edit" || recordView.type === "detail"
      ? recordView.recordId
      : null;
  const extEditId =
    extView.type === "edit" || extView.type === "detail"
      ? extView.reportId
      : null;
  const showRecordForm = recordView.type !== "list";
  const showExtForm = extView.type !== "list";
  const isRecordDetail = recordView.type === "detail";
  const isExtDetail = extView.type === "detail";
  const isRecordEditing =
    recordView.type === "create" || recordView.type === "edit";
  const isExtEditing = extView.type === "create" || extView.type === "edit";
  // Detail/create-edit split: fields editable only while editing a draft
  const recordLocked = !isRecordEditing;
  const extLocked = !isExtEditing;
  const recordAttachmentsLocked =
    isApproved(recordForm.status) ||
    Boolean(recordStamp.approvalInProgress) ||
    (!isRecordEditing && !isDraft(recordForm.status));
  const extAttachmentsLocked =
    isApproved(extForm.status) ||
    Boolean(extStamp.approvalInProgress) ||
    (!isExtEditing && !isDraft(extForm.status));

  const loadPlans = useCallback(async () => {
    try {
      setPlans(await fetchEducationPlans());
      setError("");
    } catch {
      reportError(setError, "연간 교육 계획서를 불러오지 못했습니다.");
    }
  }, []);

  const loadRecords = useCallback(async (q?: string) => {
    try {
      setRecords(await fetchEducationRecords(q));
      setError("");
    } catch {
      reportError(setError, "교육 기록부를 불러오지 못했습니다.");
    }
  }, []);

  const loadExternals = useCallback(async (q?: string) => {
    try {
      setExternals(await fetchExternalEducationReports(q));
      setError("");
    } catch {
      reportError(setError, "사외교육 보고서를 불러오지 못했습니다.");
    }
  }, []);

  const loadOrgMembers = useCallback(async () => {
    try {
      setOrgMembers(await fetchOrgMembers());
    } catch {
      setOrgMembers([]);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
    void loadRecords();
    void loadExternals();
    void loadOrgMembers();
  }, [companyId, loadPlans, loadRecords, loadExternals, loadOrgMembers]);

  const departmentOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; departmentId: number | null }>();
    for (const m of orgMembers) {
      const id = m.departmentId ?? null;
      const label = m.departmentName?.trim() || "미배치";
      const key = id != null ? `d-${id}` : UNASSIGNED_DEPT;
      if (!map.has(key)) map.set(key, { key, label, departmentId: id });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ko"),
    );
  }, [orgMembers]);

  const membersByDeptKey = useCallback(
    (deptKey: string) => {
      return orgMembers.filter((m) => {
        const key =
          m.departmentId != null ? `d-${m.departmentId}` : UNASSIGNED_DEPT;
        return key === deptKey;
      });
    },
    [orgMembers],
  );

  const deptKeyOfRow = (row: EducationAttendee) => {
    if (row.departmentId != null) return `d-${row.departmentId}`;
    if (!row.dept?.trim() || row.dept === "미배치") {
      // legacy: try match by name
      if (row.userId != null) {
        const m = orgMembers.find((x) => x.userId === row.userId);
        if (m)
          return m.departmentId != null
            ? `d-${m.departmentId}`
            : UNASSIGNED_DEPT;
      }
      if (row.dept?.trim() === "미배치" || !row.dept?.trim())
        return row.dept || row.userId ? UNASSIGNED_DEPT : "";
      const byName = departmentOptions.find((d) => d.label === row.dept);
      return byName?.key || "";
    }
    const byName = departmentOptions.find((d) => d.label === row.dept);
    return byName?.key || "";
  };

  const selectedUserIds = useMemo(() => {
    const set = new Set<number>();
    attendeeRows.forEach((r) => {
      if (r.userId != null) set.add(r.userId);
    });
    return set;
  }, [attendeeRows]);

  useEffect(() => {
    const t = window.setTimeout(() => void loadRecords(recordKeyword), 250);
    return () => window.clearTimeout(t);
  }, [recordKeyword, loadRecords]);

  useEffect(() => {
    const t = window.setTimeout(() => void loadExternals(extKeyword), 250);
    return () => window.clearTimeout(t);
  }, [extKeyword, loadExternals]);

  useEffect(() => {
    if (!showRecordForm || recordEditId == null) return;
    const latest = records.find((r) => r.recordId === recordEditId);
    if (!latest) return;
    if (latest.attachments) setRecordAttachments(latest.attachments);
    setRecordStamp(stampFrom(latest));
    setRecordForm((p) => ({ ...p, status: normalizeStatus(latest.status) }));
  }, [records, recordEditId, showRecordForm]);

  useEffect(() => {
    if (!showExtForm || extEditId == null) return;
    const latest = externals.find((r) => r.reportId === extEditId);
    if (!latest) return;
    if (latest.attachments) setExtAttachments(latest.attachments);
    setExtStamp(stampFrom(latest));
    setExtForm((p) => ({ ...p, status: normalizeStatus(latest.status) }));
  }, [externals, extEditId, showExtForm]);

  const openRecordCreate = async () => {
    setRecordAttachments([]);
    setRecordPendingFiles([]);
    setRecordStamp(EMPTY_STAMP);
    setRecordApprovalOpen(false);
    setApprovalMessage("");
    setRecordForm({
      recordNo: "",
      educationDate: new Date().toISOString().slice(0, 10),
      startTime: "",
      endTime: "",
      title: "",
      educationType: "INTERNAL",
      instructor: "",
      place: "",
      hours: "",
      attendees: "[]",
      content: "",
      resultText: "",
      status: "DRAFT",
      remark: "",
    });
    setAttendeeRows(defaultEducationAttendees());
    setRecordHint("저장 시 문서번호가 자동 발급됩니다.");
    setRecordView({ type: "create" });
  };

  const fillRecordForm = (row: EducationRecord) => {
    setRecordHint("");
    setRecordAttachments(row.attachments || []);
    setRecordPendingFiles([]);
    setRecordStamp(stampFrom(row));
    setRecordApprovalOpen(false);
    setApprovalMessage("");
    const attendees = parseEducationAttendees(row.attendees);
    setAttendeeRows(attendees);
    setRecordForm({
      recordNo: row.recordNo || "",
      educationDate: row.educationDate || "",
      startTime: toTimeInputValue(row.startTime),
      endTime: toTimeInputValue(row.endTime),
      title: row.title || "",
      educationType: row.educationType || "INTERNAL",
      instructor: row.instructor || "",
      place: row.place || "",
      hours: row.hours || "",
      attendees: serializeEducationAttendees(attendees),
      content: row.content || "",
      resultText: row.resultText || "",
      status: normalizeStatus(row.status),
      remark: row.remark || "",
    });
  };

  const openRecordDetail = (row: EducationRecord) => {
    fillRecordForm(row);
    setRecordView({ type: "detail", recordId: row.recordId });
  };

  const applyRecordSaved = (saved: EducationRecord) => {
    setRecordStamp(stampFrom(saved));
    setRecordAttachments(saved.attachments || []);
    const attendees = parseEducationAttendees(saved.attendees);
    setAttendeeRows(attendees);
    setRecordForm((p) => ({
      ...p,
      recordNo: saved.recordNo || p.recordNo,
      attendees: serializeEducationAttendees(attendees),
      status: normalizeStatus(saved.status),
    }));
    setRecordView({ type: "detail", recordId: saved.recordId });
  };

  const patchAttendee = (idx: number, patch: Partial<EducationAttendee>) => {
    setAttendeeRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };

  const saveRecord = async (e: FormEvent) => {
    e.preventDefault();
    if (recordLocked) {
      reportError(setError, "승인된 기록은 수정할 수 없습니다.");
      return;
    }
    if (!recordForm.title.trim()) {
      reportError(setError, "교육명을 입력해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const { status: _status, ...rest } = recordForm;
      const payload = {
        ...rest,
        attendees: serializeEducationAttendees(attendeeRows),
      };
      let recordId = recordEditId;
      if (recordId != null) {
        const updated = await updateEducationRecord(recordId, payload);
        applyRecordSaved(updated);
      } else {
        const created = await createEducationRecord(payload);
        recordId = created.recordId;
        applyRecordSaved(created);
      }
      if (recordPendingFiles.length > 0 && recordId != null) {
        const uploaded = await uploadEducationRecordAttachments(
          recordId,
          recordPendingFiles,
        );
        setRecordAttachments((prev) => [...prev, ...uploaded]);
        setRecordPendingFiles([]);
      }
      await loadRecords(recordKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "교육 기록 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRecordUploadNow = async (files: FileList | null) => {
    if (!files?.length || recordAttachmentsLocked) return;
    const selected = Array.from(files);
    if (recordFileRef.current) recordFileRef.current.value = "";
    if (recordEditId == null) {
      setRecordPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadEducationRecordAttachments(
        recordEditId,
        selected,
      );
      setRecordAttachments((prev) => [...prev, ...uploaded]);
      await loadRecords(recordKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRecordAttachment = async (
    attachment: EducationRecordAttachment,
  ) => {
    if (recordEditId == null || recordAttachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteEducationRecordAttachment(
        recordEditId,
        attachment.attachmentId,
      );
      setRecordAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await loadRecords(recordKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const approveRecord = async () => {
    if (recordEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "기록을 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    )
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await approveEducationRecord(recordEditId);
      applyRecordSaved(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadRecords(recordKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeRecord = async () => {
    if (recordEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeEducationRecord(recordEditId);
      applyRecordSaved(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadRecords(recordKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadRecordPdf = async () => {
    if (recordEditId == null) return;
    try {
      setPdfDownloading(true);
      const name =
        recordForm.recordNo?.trim() ||
        recordForm.title?.trim() ||
        String(recordEditId);
      await downloadEducationRecordPdf(recordEditId, `교육일지_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const openExtCreate = async () => {
    setExtAttachments([]);
    setExtPendingFiles([]);
    setExtStamp(EMPTY_STAMP);
    setExtApprovalOpen(false);
    setApprovalMessage("");
    setExtForm({
      reportNo: "",
      courseName: "",
      organizer: "",
      periodFrom: "",
      periodTo: "",
      traineeName: "",
      department: "",
      hours: "",
      costText: "",
      content: "",
      applicationPlan: "",
      status: "DRAFT",
      remark: "",
    });
    setExtHint("저장 시 문서번호가 자동 발급됩니다.");
    setExtView({ type: "create" });
  };

  const fillExtForm = (row: ExternalEducationReport) => {
    setExtHint("");
    setExtAttachments(row.attachments || []);
    setExtPendingFiles([]);
    setExtStamp(stampFrom(row));
    setExtApprovalOpen(false);
    setApprovalMessage("");
    setExtForm({
      reportNo: row.reportNo || "",
      courseName: row.courseName || "",
      organizer: row.organizer || "",
      periodFrom: row.periodFrom || "",
      periodTo: row.periodTo || "",
      traineeName: row.traineeName || "",
      department: row.department || "",
      hours: row.hours || "",
      costText: row.costText || "",
      content: row.content || "",
      applicationPlan: row.applicationPlan || "",
      status: normalizeStatus(row.status),
      remark: row.remark || "",
    });
  };

  const openExtDetail = (row: ExternalEducationReport) => {
    fillExtForm(row);
    setExtView({ type: "detail", reportId: row.reportId });
  };

  const applyExtSaved = (saved: ExternalEducationReport) => {
    setExtStamp(stampFrom(saved));
    setExtAttachments(saved.attachments || []);
    setExtForm((p) => ({
      ...p,
      reportNo: saved.reportNo || p.reportNo,
      status: normalizeStatus(saved.status),
    }));
    setExtView({ type: "detail", reportId: saved.reportId });
  };

  const saveExt = async (e: FormEvent) => {
    e.preventDefault();
    if (extLocked) {
      reportError(setError, "승인된 보고서는 수정할 수 없습니다.");
      return;
    }
    if (!extForm.courseName.trim()) {
      reportError(setError, "과정명을 입력해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const { status: _status, ...rest } = extForm;
      const payload = { ...rest };
      let reportId = extEditId;
      if (reportId != null) {
        const updated = await updateExternalEducationReport(reportId, payload);
        applyExtSaved(updated);
      } else {
        const created = await createExternalEducationReport(payload);
        reportId = created.reportId;
        applyExtSaved(created);
      }
      if (extPendingFiles.length > 0 && reportId != null) {
        const uploaded = await uploadExternalEducationReportAttachments(
          reportId,
          extPendingFiles,
        );
        setExtAttachments((prev) => [...prev, ...uploaded]);
        setExtPendingFiles([]);
      }
      await loadExternals(extKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "사외교육 보고서 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleExtUploadNow = async (files: FileList | null) => {
    if (!files?.length || extAttachmentsLocked) return;
    const selected = Array.from(files);
    if (extFileRef.current) extFileRef.current.value = "";
    if (extEditId == null) {
      setExtPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadExternalEducationReportAttachments(
        extEditId,
        selected,
      );
      setExtAttachments((prev) => [...prev, ...uploaded]);
      await loadExternals(extKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExtAttachment = async (
    attachment: ExternalEducationReportAttachment,
  ) => {
    if (extEditId == null || extAttachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteExternalEducationReportAttachment(
        extEditId,
        attachment.attachmentId,
      );
      setExtAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await loadExternals(extKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const approveExt = async () => {
    if (extEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "보고서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    )
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await approveExternalEducationReport(extEditId);
      applyExtSaved(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadExternals(extKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeExt = async () => {
    if (extEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeExternalEducationReport(extEditId);
      applyExtSaved(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadExternals(extKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadExtPdf = async () => {
    if (extEditId == null) return;
    try {
      setPdfDownloading(true);
      const name =
        extForm.reportNo?.trim() ||
        extForm.courseName?.trim() ||
        String(extEditId);
      await downloadExternalEducationReportPdf(
        extEditId,
        `외부교육보고서_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderRecordDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(recordForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadRecordPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {isApproved(recordForm.status) && recordStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeRecord()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {recordStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={recordStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadRecords(recordKeyword);
        }}
      />
      {isDraft(recordForm.status) && !recordStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (recordEditId == null) return;
              setRecordHint("저장하면 상세보기로 이동합니다.");
              setRecordView({
                type: "edit",
                recordId: recordEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setRecordApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveRecord()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (recordEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteEducationRecord(recordEditId).then(() => {
                setRecordView({ type: "list" });
                void loadRecords(recordKeyword);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setRecordView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderRecordEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          recordView.type === "edit" && recordEditId != null
            ? setRecordView({
                type: "detail",
                recordId: recordEditId,
              })
            : setRecordView({ type: "list" })
        }
      >
        {recordView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderExtDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(extForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadExtPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {isApproved(extForm.status) && extStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeExt()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {extStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={extStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadExternals(extKeyword);
        }}
      />
      {isDraft(extForm.status) && !extStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (extEditId == null) return;
              setExtHint("저장하면 상세보기로 이동합니다.");
              setExtView({ type: "edit", reportId: extEditId });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setExtApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveExt()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (extEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteExternalEducationReport(extEditId).then(() => {
                setExtView({ type: "list" });
                void loadExternals(extKeyword);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setExtView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderExtEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          extView.type === "edit" && extEditId != null
            ? setExtView({ type: "detail", reportId: extEditId })
            : setExtView({ type: "list" })
        }
      >
        {extView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>교육</h2>
          <p>연간 교육 계획서 · 교육 기록부 · 사외교육 수강 보고서</p>
        </div>
      </div>

      <div className="quality-tabs">
        {(
          [
            ["plan", "연간 교육 계획서"],
            ["record", "교육 기록부"],
            ["external", "사외교육 수강 보고서"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`quality-tabs__btn${tab === key ? " is-active" : ""}`}
            onClick={() => {
              setTab(key);
              setRecordView(RECORD_LIST);
              setExtView(EXT_LIST);
              setRecordApprovalOpen(false);
              setExtApprovalOpen(false);
              setApprovalMessage("");
              setError("");
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="quality-error">{error}</p>}

      {tab === "plan" && (
        <EducationAnnualPlanTab
          plans={plans}
          onReload={loadPlans}
          onError={setError}
        />
      )}

      {tab === "record" && (
        <>
          {!showRecordForm ? (
            <>
              <div className="quality-shell__toolbar">
                <input
                  value={recordKeyword}
                  onChange={(e) => setRecordKeyword(e.target.value)}
                  placeholder="번호 / 교육명 / 강사 검색"
                  style={{
                    height: 34,
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                  }}
                />
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openRecordCreate()}
                >
                  기록 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>문서번호</th>
                      <th>일자·시간</th>
                      <th>교육명</th>
                      <th>구분</th>
                      <th>강사</th>
                      <th>첨부</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={8}>등록된 기록이 없습니다.</td>
                      </tr>
                    ) : (
                      records.map((row) => {
                        const status = normalizeStatus(row.status);
                        return (
                          <tr
                            key={row.recordId}
                            onClick={() => openRecordDetail(row)}
                          >
                            <td>{row.recordNo || "-"}</td>
                            <td>
                              {formatEducationSchedule(
                                row.educationDate,
                                row.startTime,
                                row.endTime,
                              )}
                            </td>
                            <td>{row.title || "-"}</td>
                            <td>
                              {row.educationType === "EXTERNAL"
                                ? "사외"
                                : "사내"}
                            </td>
                            <td>{row.instructor || "-"}</td>
                            <td>{row.attachmentCount ?? 0}</td>
                            <td>
                              <span
                                className={`quality-badge is-${statusBadgeClass(status, row.approvalInProgress)}`}
                              >
                                {statusLabel(status, row.approvalInProgress)}
                              </span>
                            </td>
                            <td>
                              {status === "DRAFT" && !row.approvalInProgress ? (
                                <button
                                  type="button"
                                  className="ncr-btn is-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!window.confirm("삭제할까요?")) return;
                                    void deleteEducationRecord(row.recordId)
                                      .then(() => loadRecords(recordKeyword))
                                      .catch(() =>
                                        reportError(
                                          setError,
                                          "삭제에 실패했습니다.",
                                        ),
                                      );
                                  }}
                                >
                                  삭제
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={
                isRecordEditing ? saveRecord : (e) => e.preventDefault()
              }
            >
              {isRecordDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>교육 기록 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {recordForm.recordNo || "-"} ·{" "}
                      {statusLabel(
                        recordForm.status,
                        recordStamp.approvalInProgress,
                      )}
                    </p>
                  </div>
                  {renderRecordDetailActions()}
                </div>
              ) : (
                renderRecordEditActions()
              )}

              {approvalMessage ? (
                <p className="quality-form__hint" style={{ color: "#1a5fb4" }}>
                  {approvalMessage}
                </p>
              ) : null}

              <section className="quality-form__section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>
                      {recordView.type === "create"
                        ? "교육 기록 등록"
                        : recordView.type === "edit"
                          ? "교육 기록 수정"
                          : "교육 기록"}
                    </h3>
                    {isRecordEditing ? (
                      <p
                        className="quality-form__hint"
                        style={{ margin: "4px 0 0" }}
                      >
                        저장하면 상세보기로 이동합니다.
                      </p>
                    ) : null}
                  </div>
                  <StampBox
                    authorName={recordStamp.authorName}
                    reviewerName={recordStamp.reviewerName}
                    approverName={recordStamp.approverName}
                  />
                </div>
                <div className="quality-form__grid">
                  <label>
                    문서번호
                    <input
                      value={
                        recordView.type === "create"
                          ? ""
                          : recordForm.recordNo || ""
                      }
                      readOnly
                      placeholder={
                        recordView.type === "create"
                          ? "저장 시 자동 발급"
                          : undefined
                      }
                    />
                    {recordView.type === "create" && recordHint ? (
                      <span className="quality-form__hint">{recordHint}</span>
                    ) : null}
                  </label>
                  <label>
                    교육일
                    <input
                      type="date"
                      value={recordForm.educationDate || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          educationDate: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    시작 시간
                    <input
                      type="time"
                      value={recordForm.startTime || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          startTime: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    종료 시간
                    <input
                      type="time"
                      value={recordForm.endTime || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          endTime: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    교육명 *
                    <input
                      value={recordForm.title}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({ ...p, title: e.target.value }))
                      }
                      required
                    />
                  </label>
                  <label>
                    구분
                    <select
                      value={recordForm.educationType || "INTERNAL"}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          educationType: e.target.value,
                        }))
                      }
                    >
                      <option value="INTERNAL">사내</option>
                      <option value="EXTERNAL">사외</option>
                    </select>
                  </label>
                  <label>
                    강사
                    <input
                      value={recordForm.instructor || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          instructor: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    장소
                    <input
                      value={recordForm.place || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({ ...p, place: e.target.value }))
                      }
                    />
                  </label>
                  <label>
                    이수시간
                    <input
                      value={recordForm.hours || ""}
                      disabled={recordLocked}
                      placeholder="예: 2시간"
                      onChange={(e) =>
                        setRecordForm((p) => ({ ...p, hours: e.target.value }))
                      }
                    />
                  </label>
                </div>
              </section>

              <section className="quality-form__section">
                <div className="quality-form__grid">
                  <label className="is-span-2">
                    교육내용
                    <textarea
                      rows={4}
                      value={recordForm.content || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          content: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    교육결과
                    <textarea
                      rows={3}
                      value={recordForm.resultText || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({
                          ...p,
                          resultText: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    비고
                    <textarea
                      rows={2}
                      value={recordForm.remark || ""}
                      disabled={recordLocked}
                      onChange={(e) =>
                        setRecordForm((p) => ({ ...p, remark: e.target.value }))
                      }
                    />
                  </label>
                </div>
              </section>
              <section className="quality-form__section">
                <div
                  className="edu-plan-card__title"
                  style={{ marginBottom: 8 }}
                >
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {!recordAttachmentsLocked ? (
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      disabled={saving}
                      onClick={() => recordFileRef.current?.click()}
                    >
                      파일 추가
                    </button>
                  ) : null}
                  <input
                    ref={recordFileRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => void handleRecordUploadNow(e.target.files)}
                  />
                </div>
                <p className="edu-plan-hint">
                  출석부, 교육자료, 평가결과 등을 첨부할 수 있습니다.
                  {recordEditId == null
                    ? " 신규 작성 시 저장과 함께 업로드됩니다."
                    : ""}
                  {recordAttachmentsLocked
                    ? " 승인 후에는 첨부를 변경할 수 없습니다."
                    : ""}
                </p>
                <ul className="edu-plan-files">
                  {recordAttachments.map((file) => (
                    <li key={file.attachmentId}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <span>{formatSize(file.fileSize)}</span>
                      </div>
                      <div className="edu-plan-files__actions">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void downloadEducationRecordAttachment(
                              recordEditId!,
                              file.attachmentId,
                              file.fileName,
                            ).catch(() =>
                              reportError(
                                setError,
                                "첨부파일 다운로드에 실패했습니다.",
                              ),
                            )
                          }
                        >
                          다운로드
                        </button>
                        {!recordAttachmentsLocked ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            disabled={saving}
                            onClick={() =>
                              void handleDeleteRecordAttachment(file)
                            }
                          >
                            삭제
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {recordPendingFiles.map((file, idx) => (
                    <li key={`pending-record-${file.name}-${idx}`}>
                      <div>
                        <strong>{file.name}</strong>
                        <span>저장 대기 · {formatSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setRecordPendingFiles((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        제거
                      </button>
                    </li>
                  ))}
                  {recordAttachments.length === 0 &&
                    recordPendingFiles.length === 0 && (
                      <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                    )}
                </ul>
              </section>

              <section className="quality-form__section">
                <div className="ca-section-head">
                  <h3>참석자</h3>
                </div>
                <div className="ca-items">
                  {attendeeRows.map((row, idx) => (
                    <div className="ca-item-card" key={row.id}>
                      <div className="ca-item-card__head">
                        <strong>참석자 {idx + 1}</strong>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {!recordLocked ? (
                            <button
                              type="button"
                              className="ncr-btn is-secondary"
                              onClick={() =>
                                patchAttendee(idx, {
                                  manualEntry: !row.manualEntry,
                                  userId: null,
                                  departmentId: null,
                                  name: row.manualEntry ? "" : row.name,
                                  dept: row.manualEntry ? "" : row.dept,
                                })
                              }
                            >
                              {row.manualEntry
                                ? "조직에서 선택"
                                : "직접 입력"}
                            </button>
                          ) : null}
                          {!recordLocked ? (
                            <button
                              type="button"
                              className="ncr-btn is-danger"
                              onClick={() =>
                                setAttendeeRows((prev) => {
                                  const next = prev.filter((_, i) => i !== idx);
                                  return next.length
                                    ? next
                                    : defaultEducationAttendees();
                                })
                              }
                            >
                              삭제
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="ca-item-card__grid">
                        <label className="is-wide">
                          부서
                          {recordLocked || row.manualEntry ? (
                            <input
                              value={row.dept}
                              readOnly={recordLocked}
                              placeholder="부서"
                              onChange={(e) =>
                                patchAttendee(idx, {
                                  dept: e.target.value,
                                  manualEntry: true,
                                  userId: null,
                                  departmentId: null,
                                })
                              }
                            />
                          ) : (
                            <select
                              value={deptKeyOfRow(row)}
                              onChange={(e) => {
                                const key = e.target.value;
                                const opt = departmentOptions.find(
                                  (d) => d.key === key,
                                );
                                patchAttendee(idx, {
                                  departmentId: opt?.departmentId ?? null,
                                  dept: opt?.label || "",
                                  userId: null,
                                  name: "",
                                  manualEntry: false,
                                });
                              }}
                            >
                              <option value="">부서 선택</option>
                              {departmentOptions.map((d) => (
                                <option key={d.key} value={d.key}>
                                  {d.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </label>
                        <label className="is-wide">
                          성명
                          {recordLocked || row.manualEntry ? (
                            <input
                              value={row.name}
                              readOnly={recordLocked}
                              placeholder="성명"
                              onChange={(e) =>
                                patchAttendee(idx, {
                                  name: e.target.value,
                                  manualEntry: true,
                                  userId: null,
                                })
                              }
                            />
                          ) : (
                            <select
                              value={
                                row.userId != null ? String(row.userId) : ""
                              }
                              disabled={!deptKeyOfRow(row)}
                              onChange={(e) => {
                                const uid = e.target.value
                                  ? Number(e.target.value)
                                  : null;
                                if (uid == null) {
                                  patchAttendee(idx, {
                                    userId: null,
                                    name: "",
                                  });
                                  return;
                                }
                                const m = orgMembers.find(
                                  (x) => x.userId === uid,
                                );
                                if (!m) return;
                                patchAttendee(idx, {
                                  userId: m.userId,
                                  name: orgMemberName(m),
                                  departmentId: m.departmentId ?? null,
                                  dept: m.departmentName?.trim() || "미배치",
                                  manualEntry: false,
                                });
                              }}
                            >
                              <option value="">
                                {deptKeyOfRow(row)
                                  ? "성명 선택"
                                  : "부서 먼저 선택"}
                              </option>
                              {deptKeyOfRow(row)
                                ? membersByDeptKey(deptKeyOfRow(row)).map(
                                    (m) => {
                                      const taken =
                                        selectedUserIds.has(m.userId) &&
                                        row.userId !== m.userId;
                                      return (
                                        <option
                                          key={m.userId}
                                          value={m.userId}
                                          disabled={taken}
                                        >
                                          {orgMemberName(m)}
                                          {m.jobTitle
                                            ? ` · ${m.jobTitle}`
                                            : ""}
                                          {taken ? " (이미 추가됨)" : ""}
                                        </option>
                                      );
                                    },
                                  )
                                : null}
                            </select>
                          )}
                        </label>
                        <div className="is-full">
                          <SignaturePadField
                            label="참석 서명"
                            value={row.signatureData}
                            disabled={recordLocked}
                            onChange={(signatureData) =>
                              patchAttendee(idx, { signatureData })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!recordLocked ? (
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        setAttendeeRows((prev) => [
                          ...prev,
                          createEmptyEducationAttendee(),
                        ])
                      }
                    >
                      참석자 추가
                    </button>
                  </div>
                ) : null}
              </section>

              {isRecordDetail ? (
                <div className="quality-form__actions">
                  {renderRecordDetailActions()}
                </div>
              ) : (
                renderRecordEditActions()
              )}

              {recordEditId != null ? (
                <ApprovalRequestModal
                  open={recordApprovalOpen}
                  titleDefault={`${recordForm.title || "교육 기록"} 결재`}
                  targetType={APPROVAL_TARGET_EDUCATION_RECORD}
                  targetId={recordEditId}
                  onClose={() => setRecordApprovalOpen(false)}
                  onSubmitted={() => {
                    setRecordApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadRecords(recordKeyword);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "external" && (
        <>
          {!showExtForm ? (
            <>
              <div className="quality-shell__toolbar">
                <input
                  value={extKeyword}
                  onChange={(e) => setExtKeyword(e.target.value)}
                  placeholder="번호 / 과정명 / 수강자 검색"
                  style={{
                    height: 34,
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                  }}
                />
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openExtCreate()}
                >
                  보고서 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>문서번호</th>
                      <th>과정명</th>
                      <th>수강자</th>
                      <th>기간</th>
                      <th>첨부</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {externals.length === 0 ? (
                      <tr>
                        <td colSpan={7}>등록된 보고서가 없습니다.</td>
                      </tr>
                    ) : (
                      externals.map((row) => {
                        const status = normalizeStatus(row.status);
                        return (
                          <tr
                            key={row.reportId}
                            onClick={() => openExtDetail(row)}
                          >
                            <td>{row.reportNo || "-"}</td>
                            <td>{row.courseName || "-"}</td>
                            <td>{row.traineeName || "-"}</td>
                            <td>
                              {row.periodFrom || "-"} ~ {row.periodTo || "-"}
                            </td>
                            <td>{row.attachmentCount ?? 0}</td>
                            <td>
                              <span
                                className={`quality-badge is-${statusBadgeClass(status, row.approvalInProgress)}`}
                              >
                                {statusLabel(status, row.approvalInProgress)}
                              </span>
                            </td>
                            <td>
                              {status === "DRAFT" && !row.approvalInProgress ? (
                                <button
                                  type="button"
                                  className="ncr-btn is-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!window.confirm("삭제할까요?")) return;
                                    void deleteExternalEducationReport(
                                      row.reportId,
                                    )
                                      .then(() => loadExternals(extKeyword))
                                      .catch(() =>
                                        reportError(
                                          setError,
                                          "삭제에 실패했습니다.",
                                        ),
                                      );
                                  }}
                                >
                                  삭제
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={isExtEditing ? saveExt : (e) => e.preventDefault()}
            >
              {isExtDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>사외교육 보고서 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {extForm.reportNo || "-"} ·{" "}
                      {statusLabel(extForm.status, extStamp.approvalInProgress)}
                    </p>
                  </div>
                  {renderExtDetailActions()}
                </div>
              ) : (
                renderExtEditActions()
              )}

              {approvalMessage ? (
                <p className="quality-form__hint" style={{ color: "#1a5fb4" }}>
                  {approvalMessage}
                </p>
              ) : null}

              <section className="quality-form__section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>
                      {extView.type === "create"
                        ? "사외교육 보고서 등록"
                        : extView.type === "edit"
                          ? "사외교육 보고서 수정"
                          : "사외교육 수강 보고서"}
                    </h3>
                    {isExtEditing ? (
                      <p
                        className="quality-form__hint"
                        style={{ margin: "4px 0 0" }}
                      >
                        저장하면 상세보기로 이동합니다.
                      </p>
                    ) : null}
                  </div>
                  <StampBox
                    authorName={extStamp.authorName}
                    reviewerName={extStamp.reviewerName}
                    approverName={extStamp.approverName}
                  />
                </div>
                <div className="quality-form__grid">
                  <label>
                    문서번호
                    <input
                      value={
                        extView.type === "create" ? "" : extForm.reportNo || ""
                      }
                      readOnly
                      placeholder={
                        extView.type === "create"
                          ? "저장 시 자동 발급"
                          : undefined
                      }
                    />
                    {extView.type === "create" && extHint ? (
                      <span className="quality-form__hint">{extHint}</span>
                    ) : null}
                  </label>
                  <label className="is-span-2">
                    과정명 *
                    <input
                      value={extForm.courseName}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({
                          ...p,
                          courseName: e.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    주관기관
                    <input
                      value={extForm.organizer || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, organizer: e.target.value }))
                      }
                    />
                  </label>
                  <label>
                    수강자
                    <input
                      value={extForm.traineeName || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({
                          ...p,
                          traineeName: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    시작일
                    <input
                      type="date"
                      value={extForm.periodFrom || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({
                          ...p,
                          periodFrom: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    종료일
                    <input
                      type="date"
                      value={extForm.periodTo || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, periodTo: e.target.value }))
                      }
                    />
                  </label>
                  <label>
                    부서
                    <input
                      value={extForm.department || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({
                          ...p,
                          department: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    시간
                    <input
                      value={extForm.hours || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, hours: e.target.value }))
                      }
                    />
                  </label>
                  <label>
                    비용
                    <input
                      value={extForm.costText || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, costText: e.target.value }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    교육내용
                    <textarea
                      rows={4}
                      value={extForm.content || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, content: e.target.value }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    업무 적용 계획
                    <textarea
                      rows={3}
                      value={extForm.applicationPlan || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({
                          ...p,
                          applicationPlan: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="is-span-2">
                    비고
                    <textarea
                      rows={2}
                      value={extForm.remark || ""}
                      disabled={extLocked}
                      onChange={(e) =>
                        setExtForm((p) => ({ ...p, remark: e.target.value }))
                      }
                    />
                  </label>
                </div>
              </section>
              <section className="quality-form__section">
                <div
                  className="edu-plan-card__title"
                  style={{ marginBottom: 8 }}
                >
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {!extAttachmentsLocked ? (
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      disabled={saving}
                      onClick={() => extFileRef.current?.click()}
                    >
                      파일 추가
                    </button>
                  ) : null}
                  <input
                    ref={extFileRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => void handleExtUploadNow(e.target.files)}
                  />
                </div>
                <p className="edu-plan-hint">
                  수료증, 강의자료, 영수증 등을 첨부할 수 있습니다.
                  {extEditId == null
                    ? " 신규 작성 시 저장과 함께 업로드됩니다."
                    : ""}
                  {extAttachmentsLocked
                    ? " 승인 후에는 첨부를 변경할 수 없습니다."
                    : ""}
                </p>
                <ul className="edu-plan-files">
                  {extAttachments.map((file) => (
                    <li key={file.attachmentId}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <span>{formatSize(file.fileSize)}</span>
                      </div>
                      <div className="edu-plan-files__actions">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void downloadExternalEducationReportAttachment(
                              extEditId!,
                              file.attachmentId,
                              file.fileName,
                            ).catch(() =>
                              reportError(
                                setError,
                                "첨부파일 다운로드에 실패했습니다.",
                              ),
                            )
                          }
                        >
                          다운로드
                        </button>
                        {!extAttachmentsLocked ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            disabled={saving}
                            onClick={() => void handleDeleteExtAttachment(file)}
                          >
                            삭제
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {extPendingFiles.map((file, idx) => (
                    <li key={`pending-ext-${file.name}-${idx}`}>
                      <div>
                        <strong>{file.name}</strong>
                        <span>저장 대기 · {formatSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setExtPendingFiles((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        제거
                      </button>
                    </li>
                  ))}
                  {extAttachments.length === 0 &&
                    extPendingFiles.length === 0 && (
                      <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                    )}
                </ul>
              </section>

              {isExtDetail ? (
                <div className="quality-form__actions">
                  {renderExtDetailActions()}
                </div>
              ) : (
                renderExtEditActions()
              )}

              {extEditId != null ? (
                <ApprovalRequestModal
                  open={extApprovalOpen}
                  titleDefault={`${extForm.courseName || "사외교육 수강 보고서"} 결재`}
                  targetType={APPROVAL_TARGET_EXTERNAL_EDUCATION}
                  targetId={extEditId}
                  onClose={() => setExtApprovalOpen(false)}
                  onSubmitted={() => {
                    setExtApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadExternals(extKeyword);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}
    </main>
  );
}

export default EducationPanel;
